/* import __COLOCATED_TEMPLATE__ from './header.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import type ArticleContent from 'embercom/models/articles/article-content';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RouterService from '@ember/routing/router-service';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type IntlService from 'ember-intl/services/intl';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';

interface Args {
  activeContent: ArticleContent;
  editActiveContent: () => void;
  unpublishActiveContent: () => void;
  showSidePanel: boolean;
  toggleSidePanel: () => void;
  publishActiveContent: () => Promise<void>;
  scheduleActiveContentUpdate: (params: {
    mode: ScheduleMode;
    scheduledDate: Date;
  }) => Promise<void>;
  onClose?: () => void;
  showDeleteButton?: boolean;
  showEditButton?: boolean;
  showPublishButton?: boolean;
  showUnpublishButton?: boolean;
  showLanguageSelector?: boolean;
  showReadOnlyButton?: boolean;
  showViewInKnowledgeButton?: boolean;
}

export default class Header extends Component<Args> {
  closeSidePanelOnResize = true;

  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare permissionsService: any;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked showDeleteConfirmModal = false;

  get showDeleteButton() {
    return this.args.showDeleteButton ?? true;
  }

  get showEditButton() {
    return this.args.showEditButton ?? true;
  }

  get showPublishButton() {
    return this.args.showPublishButton ?? true;
  }

  get showUnpublishButton() {
    return this.args.showUnpublishButton ?? true;
  }

  get showLanguageSelector() {
    if (this.appService.app.isStandaloneApp) {
      return false;
    }
    return this.args.showLanguageSelector ?? true;
  }

  get showReadOnlyButton() {
    return this.args.showReadOnlyButton ?? true;
  }

  get showViewInKnowledgeButton() {
    return this.args.showViewInKnowledgeButton ?? false;
  }

  get canUseScheduledPublishing() {
    return this.appService.app.canUseScheduledPublishing;
  }

  get scheduledContentUpdates() {
    let updates = this.args.activeContent.scheduledContentUpdates;
    let updatesArray: ScheduledContentUpdate[] = Array.isArray(updates)
      ? updates
      : (updates as any)?.toArray() || [];
    return updatesArray.filter(
      (update: ScheduledContentUpdate) => update.status === ScheduledContentUpdateStatus.PENDING,
    );
  }

  get scheduledPublishUpdates() {
    return this.scheduledContentUpdates.filter(
      (update: ScheduledContentUpdate) => update.contentUpdateType === 'publish',
    );
  }

  get scheduledUnpublishUpdates() {
    return this.scheduledContentUpdates.filter(
      (update: ScheduledContentUpdate) => update.contentUpdateType === 'unpublish',
    );
  }

  get hasScheduledContentUpdates() {
    return this.scheduledContentUpdates.length > 0;
  }

  get hasScheduledPublishUpdates() {
    return this.scheduledPublishUpdates.length > 0;
  }

  get hasScheduledUnpublishUpdates() {
    return this.scheduledUnpublishUpdates.length > 0;
  }

  get isPublishing() {
    return this.knowledgeHubEditorService.isPublishing;
  }

  get scheduleDisplayText() {
    if (!this.hasScheduledContentUpdates) {
      return null;
    }

    // Show unpublish schedule if content is published, otherwise show publish schedule
    let updateToShow = this.args.activeContent.isPublished
      ? this.scheduledUnpublishUpdates[0]
      : this.scheduledPublishUpdates[0];

    if (!updateToShow) {
      return null;
    }

    let result = ScheduledContentUpdate.getScheduleDateAndTime(
      updateToShow,
      this.appService.app.timezone,
      this.intl,
    );

    if (!result) {
      return null;
    }

    let [date, time] = result;
    let translationKey = this.args.activeContent.isPublished
      ? 'knowledge-hub.content-editor.article-content.schedule-unpublish-modal.current-schedule-short'
      : 'knowledge-hub.content-editor.article-content.schedule-modal.current-schedule-short';

    return this.intl.t(translationKey, { date, time });
  }

  @action
  viewInKnowledge() {
    safeWindowOpen(
      this.router.urlFor(
        'apps.app.knowledge-hub.view',
        objectNames[this.args.activeContent.entityType],
        this.args.activeContent.entityId,
      ),
      '_blank',
    );
  }

  @action closeSidePanel(header: HTMLElement) {
    let headerHasScroll = header.offsetWidth < header.scrollWidth;
    let closeSidePanel = headerHasScroll && this.closeSidePanelOnResize && this.args.showSidePanel;
    this.closeSidePanelOnResize = !headerHasScroll && this.args.showSidePanel;
    if (closeSidePanel) {
      // schedule the side panel to close after it finishes opening, to avoid a race condition
      schedule('afterRender', () => {
        this.args.toggleSidePanel();
      });
    }
  }

  @action async showDeleteConfirmationModal() {
    let activeContent = this.knowledgeHubEditorService.activeContentModel;

    try {
      await this.permissionsService.checkPermission(
        activeContent.requiredDeletePermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }
    this.showDeleteConfirmModal = true;
  }

  @dropTask
  *deleteActiveContent() {
    this.showDeleteConfirmModal = false;
    yield this.knowledgeHubEditorService.deleteActiveContent();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::View::Header': typeof Header;
    'knowledge-hub/content-editor/article-content/view/header': typeof Header;
  }
}
